import React, { useMemo } from 'react';

/**
 * MITRE ATT&CK Coverage Matrix
 *
 * Renders the full enterprise ATT&CK tactic set (14 tactics in kill-chain
 * order) as a heat-graded grid. Tactics with observed activity glow with
 * intensity proportional to their incident count; tactics with zero activity
 * appear as dim "ghost" cells so coverage *gaps* are visually apparent.
 *
 * Why this matters in a SOC report: the heatmap answers two CISO-level
 * questions in a single glance -
 *   1. "What is hitting us?"  (the bright cells)
 *   2. "What aren't we seeing?"  (the dim cells)
 *
 * Microsoft Sentinel emits tactic names in PascalCase ("DefenseEvasion",
 * "CommandAndControl") in `AdditionalData.tactics`. We normalise on lookup
 * so a variety of casings ("Defense Evasion", "Command & Control", etc.)
 * all map to the same canonical column.
 */

export interface MitreTacticCount {
  tactic: string;
  count: number;
}

interface Props {
  data: MitreTacticCount[];
  title?: string;
  /** Period label for the subtitle, e.g. "today", "this week", "this month". */
  periodLabel?: string;
}

interface CanonicalTactic {
  id: string;
  name: string;
  /** Lookup key - lowercased, alphabetic-only, with `&` expanded to `and`. */
  key: string;
}

const TACTICS: CanonicalTactic[] = [
  { id: 'TA0043', name: 'Reconnaissance',         key: 'reconnaissance' },
  { id: 'TA0042', name: 'Resource Development',   key: 'resourcedevelopment' },
  { id: 'TA0001', name: 'Initial Access',         key: 'initialaccess' },
  { id: 'TA0002', name: 'Execution',              key: 'execution' },
  { id: 'TA0003', name: 'Persistence',            key: 'persistence' },
  { id: 'TA0004', name: 'Privilege Escalation',   key: 'privilegeescalation' },
  { id: 'TA0005', name: 'Defense Evasion',        key: 'defenseevasion' },
  { id: 'TA0006', name: 'Credential Access',      key: 'credentialaccess' },
  { id: 'TA0007', name: 'Discovery',              key: 'discovery' },
  { id: 'TA0008', name: 'Lateral Movement',       key: 'lateralmovement' },
  { id: 'TA0009', name: 'Collection',             key: 'collection' },
  { id: 'TA0011', name: 'Command & Control',      key: 'commandandcontrol' },
  { id: 'TA0010', name: 'Exfiltration',           key: 'exfiltration' },
  { id: 'TA0040', name: 'Impact',                 key: 'impact' },
];

function normaliseTacticKey(raw: string): string {
  return raw.toLowerCase().replace(/&/g, 'and').replace(/[^a-z]/g, '');
}

/**
 * Map raw counts onto the canonical 14-tactic set. Unknown tactics (e.g.
 * "PreAttack" from older Sentinel schemas) are folded into the closest
 * canonical match where possible, otherwise dropped silently - they don't
 * fit the standard ATT&CK matrix.
 */
function buildMatrix(data: MitreTacticCount[]): { tactic: CanonicalTactic; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of data) {
    const k = normaliseTacticKey(row.tactic ?? '');
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + (row.count || 0));
  }
  return TACTICS.map((t) => ({ tactic: t, count: counts.get(t.key) ?? 0 }));
}

/**
 * Map a raw count to a heat color using a 4-stop scale relative to the
 * busiest tactic in the period. Zero stays neutral grey.
 */
function heatColor(count: number, max: number): { bg: string; fg: string; border: string } {
  if (count === 0 || max === 0) {
    return { bg: '#f8fafc', fg: '#94a3b8', border: '#e2e8f0' };
  }
  const ratio = count / max;
  if (ratio >= 0.75) return { bg: '#b91c1c', fg: '#ffffff', border: '#991b1b' }; // hot
  if (ratio >= 0.40) return { bg: '#ea580c', fg: '#ffffff', border: '#c2410c' };
  if (ratio >= 0.15) return { bg: '#fbbf24', fg: '#78350f', border: '#f59e0b' };
  return { bg: '#fef3c7', fg: '#78350f', border: '#fde68a' };                     // mild
}

export function MitreCoverageMatrix({
  data,
  title = 'MITRE ATT&CK Coverage',
  periodLabel = 'this period',
}: Props): React.ReactElement {
  const matrix = useMemo(() => buildMatrix(data), [data]);
  const max = useMemo(() => matrix.reduce((m, c) => Math.max(m, c.count), 0), [matrix]);
  const hitCount = useMemo(() => matrix.filter((c) => c.count > 0).length, [matrix]);
  const totalIncidents = useMemo(() => matrix.reduce((s, c) => s + c.count, 0), [matrix]);
  const topTactic = useMemo(
    () => matrix.reduce((best, c) => (c.count > best.count ? c : best), matrix[0]!),
    [matrix],
  );

  const coverageRatio = hitCount / TACTICS.length;
  const coverageColor =
    coverageRatio >= 0.5 ? '#b91c1c' :
    coverageRatio >= 0.25 ? '#ea580c' :
    coverageRatio > 0 ? '#f59e0b' : '#64748b';

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: 18,
        marginBottom: 20,
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Activity observed across the 14 enterprise ATT&amp;CK tactics {periodLabel}.
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: coverageColor, lineHeight: 1 }}>
            {hitCount}<span style={{ color: '#94a3b8', fontWeight: 600 }}> / {TACTICS.length}</span>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            tactics with activity
          </div>
        </div>
      </div>

      {/* ── Matrix grid ────────────────────────────────────────────── */}
      <div
        role="list"
        aria-label="MITRE ATT&CK enterprise tactic coverage"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 8,
        }}
      >
        {matrix.map(({ tactic, count }) => {
          const c = heatColor(count, max);
          const intensityLabel =
            count === 0 ? 'no activity' :
            max > 0 && count / max >= 0.75 ? 'very high activity' :
            max > 0 && count / max >= 0.40 ? 'high activity' :
            max > 0 && count / max >= 0.15 ? 'moderate activity' : 'low activity';
          const ariaLabel =
            `${tactic.name} (${tactic.id}): ${count.toLocaleString()} ` +
            `incident${count === 1 ? '' : 's'}, ${intensityLabel}.`;
          return (
            <div
              key={tactic.id}
              role="listitem"
              aria-label={ariaLabel}
              title={ariaLabel}
              style={{
                background: c.bg,
                color: c.fg,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: '10px 8px',
                minHeight: 78,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                opacity: count === 0 ? 0.65 : 1,
              }}
            >
              <div aria-hidden="true" style={{ fontSize: 9, opacity: 0.75, letterSpacing: '0.04em', fontWeight: 600 }}>
                {tactic.id}
              </div>
              <div aria-hidden="true" style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.15, marginTop: 2 }}>
                {tactic.name}
              </div>
              <div aria-hidden="true" style={{ fontSize: 20, fontWeight: 800, marginTop: 4, lineHeight: 1 }}>
                {count > 0 ? count.toLocaleString() : ' - '}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Footer summary ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginTop: 14,
          paddingTop: 12,
          borderTop: '1px solid #f1f5f9',
          fontSize: 12,
          color: '#475569',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong style={{ color: '#0f172a' }}>{totalIncidents.toLocaleString()}</strong> tactic-tagged
          incident attribution{totalIncidents === 1 ? '' : 's'} {periodLabel}
        </div>
        {totalIncidents > 0 && topTactic && topTactic.count > 0 && (
          <div>
            Most-targeted tactic: <strong style={{ color: '#0f172a' }}>{topTactic.tactic.name}</strong>
            {' '}({topTactic.count.toLocaleString()})
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ color: '#94a3b8' }}>Less</span>
          <span style={{ width: 14, height: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 2 }} />
          <span style={{ width: 14, height: 12, background: '#fbbf24', border: '1px solid #f59e0b', borderRadius: 2 }} />
          <span style={{ width: 14, height: 12, background: '#ea580c', border: '1px solid #c2410c', borderRadius: 2 }} />
          <span style={{ width: 14, height: 12, background: '#b91c1c', border: '1px solid #991b1b', borderRadius: 2 }} />
          <span style={{ color: '#94a3b8' }}>More</span>
        </div>
      </div>
    </div>
  );
}
